import { buildCoveredPeriodSection } from './coveredPeriodBrief';
import {preserveMedicationDoseText,medicationQuantities} from '../../shared/medicationDose';
import { getPreparationSourceAuthors, formatPreparationCitation } from './preparationSourceAuthors';
import { JOURNEY_ANSWER_STATUS } from './journeyAnswerModel';

const asArray = (value) => Array.isArray(value) ? value : [];
const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const cleanConfirmedText = (value = '') => String(value || '').trim();
const unique = (items = []) => [...new Set(items.map(cleanText).filter(Boolean))];

const makeFingerprint = (value) => {
  const input = JSON.stringify(value);
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `brief-${(hash >>> 0).toString(16)}`;
};

const makeConfirmedStatement = (answer) => ({
  id: answer.questionId,
  questionId: answer.questionId,
  text: cleanConfirmedText(answer.confirmedText),
  sourceIds: unique(answer.sourceIds),
  origin: answer.origin === 'caregiver_authored' ? 'caregiver_authored' : 'caregiver_confirmed',
});

const makeLegacyPriorityStatements = (confirmedPriorities = []) => asArray(confirmedPriorities)
  .map((priority, index) => ({
    id: cleanText(priority?.id) || `legacy-priority-${index + 1}`,
    questionId: 'team_understanding',
    text: cleanConfirmedText(priority?.text),
    sourceIds: unique(priority?.sourceIds),
    origin: priority?.sourceIds?.length ? 'caregiver_confirmed' : 'caregiver_authored',
    legacy: true,
  }))
  .filter((statement) => statement.text);

const splitBriefSentences = (value = '') => String(value || '')
  .match(/(?:\d\.\d|[^.!?])+[.!?]*|[^.!?]+$/g)?.map((sentence) => sentence.trim()).filter(Boolean) || [];

const BRIEF_SIMILARITY_STOP_WORDS = new Set([
  'a', 'an', 'and', 'at', 'been', 'for', 'has', 'have', 'in', 'is', 'more', 'of', 'often', 'the', 'to',
]);
const briefSentenceTokens = (sentence = '') => new Set(cleanText(sentence).toLowerCase()
  .split(/[^a-z0-9]+/)
  .filter((token) => token.length > 2 && !BRIEF_SIMILARITY_STOP_WORDS.has(token))
  .map((token) => token.endsWith('ing') && token.length > 5 ? token.slice(0, -3) : token));
const sourceSetsOverlap = (left = [], right = []) => left.some((sourceId) => right.includes(sourceId));
const sentenceSimilarity = (left, right) => {
  const leftTokens = briefSentenceTokens(left);
  const rightTokens = briefSentenceTokens(right);
  const union = new Set([...leftTokens, ...rightTokens]);
  if (!union.size) return 0;
  const intersectionSize = [...leftTokens].filter((token) => rightTokens.has(token)).length;
  return intersectionSize / union.size;
};

const removeRepeatedCustomFacts = (sections = []) => {
  const seenSentences = [];
  return sections.map((section) => ({
    ...section,
    statements: section.statements.map((statement) => {
      const uniqueSentences = splitBriefSentences(statement.text).filter((sentence) => {
        const key = cleanText(sentence).toLowerCase();
        if (!key) return false;
        const repeated = seenSentences.some((seen) => seen.key === key || (
          sourceSetsOverlap(statement.sourceIds, seen.sourceIds)
          && sentenceSimilarity(sentence, seen.sentence) >= 0.55
        ));
        if (statement.origin !== 'caregiver_authored' && repeated) return false;
        seenSentences.push({ key, sentence, sourceIds: statement.sourceIds });
        return true;
      });
      return { ...statement, text: uniqueSentences.join(' ') };
    }).filter((statement) => cleanConfirmedText(statement.text)),
  })).filter((section) => section.statements.length);
};

export const buildJourneyBrief = ({ snapshot, answers = [], confirmedPriorities = [], reportHeader = null }) => {
  const isCustomPrepare = snapshot?.templateId === 'custom_prepare';
  const confirmedByQuestion = new Map(asArray(answers)
    .filter((answer) => answer?.status === JOURNEY_ANSWER_STATUS.CONFIRMED && cleanConfirmedText(answer.confirmedText))
    .filter((answer) => (!snapshot?.evidenceHorizon?.handover && !snapshot?.evidenceWarnings?.length) || !(snapshot.answerFreshness || []).some(state => state.questionId === answer.questionId && state.currentEvidenceFingerprint !== answer.evidenceFingerprint))
    .map((answer) => [answer.questionId, makeConfirmedStatement(answer)]));

  const evidence = asArray(snapshot?.areas).flatMap(area => [...asArray(area.evidence), ...asArray(area.possibleEvidence)]);
  const mappedSections = asArray(snapshot?.briefStructure).map((section) => {
    const statement = confirmedByQuestion.get(section.questionId);
    const statements = statement ? [statement] : (
      section.legacySource === 'confirmedPriorities' && !confirmedByQuestion.has('team_understanding')
        ? makeLegacyPriorityStatements(confirmedPriorities) : []
    );
    return {
      id: cleanText(section.id),
      questionId: cleanText(section.questionId),
      title: cleanText(isCustomPrepare ? section.label : section.title),
      statements: statements.map(item => {
        const quantities = evidence
          .filter(source => item.sourceIds.includes(`${source.sourceType}:${source.sourceId}`) || item.sourceIds.includes(source.sourceId))
          .filter(source => source.sourceType === 'medication' && (source.medicationQuantities || source.record))
          .flatMap(source => source.medicationQuantities || medicationQuantities(source.record));
        return {
          ...item,
          text: preserveMedicationDoseText(item.text, quantities),
          sourceAuthors: getPreparationSourceAuthors(item.sourceIds, evidence),
        };
      }),
    };
  }).filter((section) => section.id && section.title && section.statements.length);
  const sections = isCustomPrepare
    ? removeRepeatedCustomFacts(mappedSections)
    : mappedSections;
  const coveredPeriod = buildCoveredPeriodSection(evidence);
  if (coveredPeriod) sections.push(coveredPeriod);

  const fingerprintInput = {
    reportHeader: reportHeader ? {
      title: cleanText(reportHeader.title),
      purpose: cleanText(reportHeader.purpose),
    } : null,
    sections: sections.map((section) => ({
    id: section.id,
    statements: section.statements.map((statement) => ({
      questionId: statement.questionId,
      text: statement.text,
      sourceIds: statement.sourceIds,
      origin: statement.origin,
      sourceAuthors: statement.sourceAuthors || [],
    })),
    })),
  };

  return {
    reviewedPeriodText: snapshot?.evidenceHorizon?.handover ? snapshot.evidenceHorizon.reviewedPeriodText : '',
    templateId: cleanText(snapshot?.templateId),
    templateVersion: Number(snapshot?.templateVersion) || null,
    journeyType: cleanText(snapshot?.journeyType || snapshot?.templateId),
    planId: cleanText(snapshot?.journeyPlan?.planId),
    planVersion: Number(snapshot?.journeyPlan?.planVersion) || null,
    planFingerprint: cleanText(snapshot?.journeyPlan?.planFingerprint),
    executionFingerprint: cleanText(snapshot?.journeyPlan?.executionFingerprint),
    title: cleanText(reportHeader?.title || snapshot?.title || 'Meeting brief'),
    reportHeader,
    sections,
    answerFingerprint: makeFingerprint(fingerprintInput),
  };
};

export const isJourneyBriefFinalizable = (brief) => asArray(brief?.sections)
  .some((section) => asArray(section?.statements).some((statement) => cleanConfirmedText(statement?.text)));

export const buildRecipientFacingBrief = (brief) => ({
  title: cleanText(brief?.title),
  header: brief?.reportHeader ? {
    title: cleanText(brief.reportHeader.title),
    purpose: cleanText(brief.reportHeader.purpose),
    personName: cleanText(brief.reportHeader.personName),
    preparedBy: cleanText(brief.reportHeader.preparedBy),
    preparedOn: cleanText(brief.reportHeader.preparedOn),
    trustStatement: cleanText(brief.reportHeader.trustStatement),
    footerText: cleanText(brief.reportHeader.footerText),
    ...(brief.reviewedPeriodText ? { reviewedPeriodText: brief.reviewedPeriodText } : {}),
  } : brief?.reviewedPeriodText ? { title: cleanText(brief.title), reviewedPeriodText: brief.reviewedPeriodText } : null,
  sections: asArray(brief?.sections).map((section) => ({
    id: cleanText(section?.id),
    title: cleanText(section?.title),
    text: asArray(section?.statements).map((statement) => [cleanConfirmedText(statement?.text), statement.sourceAuthors?.length ? `Sources: ${statement.sourceAuthors.map(formatPreparationCitation).join('; ')}` : ''].filter(Boolean).join('\n')).filter(Boolean).join('\n\n'),
  })).filter((section) => section.id && section.title && section.text),
});

export const buildFinalizedText = (recipientContent) => {
  const header = recipientContent?.header;
  const headerLines = header ? [
    header.title,
    header.purpose,
    header.personName ? `Prepared for: ${header.personName}` : '',
    header.preparedBy ? `Prepared by: ${header.preparedBy}` : '',
    header.preparedOn ? `Prepared on: ${header.preparedOn}` : '',
    header.trustStatement,
    header.reviewedPeriodText,
  ].filter(Boolean) : [cleanText(recipientContent?.title)].filter(Boolean);
  const sectionText = asArray(recipientContent?.sections)
    .map((section) => `${section.title}\n${section.text}`)
    .join('\n\n');
  return [...headerLines, sectionText, header?.footerText].filter(Boolean).join('\n\n');
};

export const hasJourneyBriefChanged = (brief, revision) => Boolean(revision)
  && cleanText(brief?.answerFingerprint) !== cleanText(revision?.answerFingerprint);
